'use client';

import { useEffect, useState } from 'react';

import { getFreshestCachedInsightsForUser } from './insightsCache';
import { readRotationPage, spotlightPageCount, spotlightPageSlice } from './spotlightRotation';
import type { DiscoverInsight } from './types';
import { useSession } from '@/providers/enhanced-session';

/**
 * Sentinel `userKey` for internal staff and anyone else outside the real
 * session-derived path — never a real `orgUserId`, so it can't collide with a
 * real user. External users get a `session:<id>` lane instead (see
 * `sessionUserKey`).
 */
export const UNKNOWN_USER_KEY = 'unknown-user';

/**
 * The localStorage cache lane for an external user's real session-derived
 * path. Lives here (with `UNKNOWN_USER_KEY`) so `useSessionDerivedCategory`,
 * the Ask DANI landing page, and Spotlight itself can never disagree about
 * where a user's cached batch lives.
 */
export function sessionUserKey(userId: string): string {
    return `session:${userId}`;
}

/** Deep link to Spotlight with one specific finding pre-selected. */
export function spotlightInsightHref(insightId: string): string {
    return `/discover/insights?insight=${encodeURIComponent(insightId)}`;
}

/**
 * This week's Spotlight findings for one user, read straight out of the
 * localStorage cache that Spotlight itself writes (`insightsGeneration.ts`) —
 * whichever category lane was cached most recently (an external user's
 * resolved ICP, or the generic cross-ICP sample).
 *
 * Read-only, and deliberately so: the landing page should surface what
 * Spotlight has already cached, not fire its own fetches on mount — callers
 * are expected to render nothing rather than substitute placeholder content.
 *
 * Returns at most one rotation page (3 insights) — the same window the
 * Spotlight page itself is showing, read via the lane's persisted rotation
 * cursor. A lane the user hasn't rotated yet shows its first page.
 */
export function readCachedSpotlightInsights(userKey: string): DiscoverInsight[] {
    const hit = getFreshestCachedInsightsForUser(userKey);
    if (!hit) return [];
    const insights = hit.entry.response.insights;
    const page = readRotationPage(
        hit.laneKey,
        hit.entry.response.weekOf,
        spotlightPageCount(insights.length),
    );
    return spotlightPageSlice(insights, page);
}

/**
 * Hook form of `readCachedSpotlightInsights`, resolving the user's cache lane
 * from the session.
 *
 * The read happens in an effect, never in a `useState` initialiser: these
 * components are pre-rendered server-side, where `localStorage` does not
 * exist, and a first paint that differed between server and client is exactly
 * what caused a real hydration error on the Spotlight screen before (see the
 * note in `useSessionDerivedCategory`). Starting empty and filling in on the
 * next client tick keeps the two in agreement.
 */
export function useSpotlightInsights(): DiscoverInsight[] {
    const { data: session } = useSession();
    const [insights, setInsights] = useState<DiscoverInsight[]>([]);

    const userId = session?.user?.id ?? null;
    const isInternal = session?.user?.isInternal ?? true;

    useEffect(() => {
        const userKey = !isInternal && userId ? sessionUserKey(userId) : UNKNOWN_USER_KEY;
        setInsights(readCachedSpotlightInsights(userKey));
    }, [userId, isInternal]);

    return insights;
}
